import { useCallback, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  History,
  Keyboard,
  LogOut,
  Maximize2,
  Pause,
  Play,
  RefreshCw,
  RotateCcw,
  Settings2,
  User,
  VolumeX,
  Volume2,
  Zap,
  Send,
  Sparkles,
  Wand2,
} from "lucide-react";
import * as Popover from "@radix-ui/react-popover";

import { useDebateStore } from "../../store/debateStore";
import { useAuthStore } from "../../store/authStore";
import {
  selectConsensusGauge,
  selectInterrupt,
  selectSessionLifecycle,
} from "../../store/selectors";
import { useDiscussionStream } from "../../hooks/useDiscussionStream";
import { useStartDebate } from "../../hooks/useStartDebate";

import { Button } from "../common/Button";
import { Tooltip } from "../common/Tooltip";
import { StatusPill } from "../common/StatusPill";
import { Pill } from "../common/Pill";
import { cn } from "../common/cn";
import { t } from "../../i18n";

const MIN_QUERY = 12;

interface AppHeaderProps {
  adminMode: boolean;
  onToggleTheme: () => void;
  onToggleMute: () => void;
}

function CompactConsensusGauge({
  score,
  pending,
  size = 28,
}: {
  score: number;
  pending: boolean;
  size?: number;
}) {
  const r = (size - 4) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(1, score));
  const dash = c * pct;
  const color =
    pct >= 0.85 ? "stroke-emerald-400" : pct >= 0.65 ? "stroke-amber-400" : "stroke-fg-subtle";

  return (
    <div className="flex items-center gap-2" aria-label={`Konsensüs %${Math.round(pct * 100)}`}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-hidden>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="hsl(var(--border-soft))"
          strokeWidth={2.5}
          opacity={0.55}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={2.5}
          strokeDasharray={`${dash} ${c - dash}`}
          strokeDashoffset={c / 4}
          strokeLinecap="round"
          className={cn("transition-[stroke-dasharray] duration-500 ease-out", color)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <span className="font-mono text-xs tabular-nums text-fg-strong">
        {pending ? "…" : `${Math.round(pct * 100)}%`}
      </span>
    </div>
  );
}

function BrandMark() {
  return (
    <span
      className="grid h-9 w-9 place-items-center rounded-xl bg-[linear-gradient(135deg,#6366F1_0%,#8B5CF6_100%)] text-white shadow-[0_8px_24px_-12px_hsl(var(--accent)/0.7),inset_0_1px_0_hsl(0_0%_100%/0.25)]"
      aria-hidden
    >
      <Sparkles className="h-4 w-4" />
    </span>
  );
}

export function AppHeader({ adminMode, onToggleTheme, onToggleMute }: AppHeaderProps) {
  const navigate = useNavigate();
  const authUser = useAuthStore((s) => s.user);
  const authLogout = useAuthStore((s) => s.logout);

  const lifecycle = useDebateStore(selectSessionLifecycle);
  const { round, scalarScore, pending } = useDebateStore(selectConsensusGauge);
  const maxRounds = useDebateStore((s) => s.maxRounds);
  const finalizeReason = useDebateStore((s) => s.finalizeReason);
  const interrupt = useDebateStore(selectInterrupt);
  const muted = useDebateStore((s) => s.muted);
  const query = useDebateStore((s) => s.query);
  const personasReady = useDebateStore((s) => s.wizard.personas.length > 0);
  const openWizard = useDebateStore((s) => s.openWizard);
  const resetForNew = useDebateStore((s) => s.resetForNew);
  const presentMode = useDebateStore((s) => s.presentMode);
  const togglePresentMode = useDebateStore((s) => s.togglePresentMode);
  const setShortcutsOpen = useDebateStore((s) => s.setShortcutsOpen);
  const openShortcuts = useCallback(() => setShortcutsOpen(true), [setShortcutsOpen]);
  const stream = useDiscussionStream();
  const debate = useStartDebate();
  const startBtnRef = useRef<HTMLButtonElement | null>(null);
  const pivotInputId = useId();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const [pivotOpen, setPivotOpen] = useState(false);
  const [pivotDraft, setPivotDraft] = useState("");

  const isRunning = lifecycle.status === "running" || lifecycle.status === "provisioning";
  const isFinalized = lifecycle.status === "finalized" || lifecycle.status === "interrupted";
  // If the wizard hasn't produced personas yet, the header CTA flips to a
  // "Sihirbazı Aç" affordance — clicking it opens the wizard modal instead of
  // attempting to start with an empty council.
  const requiresWizard = !isRunning && !personasReady;
  const canStart = !isRunning && !requiresWizard && query.trim().length >= MIN_QUERY;

  const onStart = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      try {
        await debate.start({
          origin: "click",
          isTrusted: e.nativeEvent.isTrusted,
          timeStamp: e.nativeEvent.timeStamp,
        });
      } catch (err) {
        console.error("[macdm] failed to start session:", err);
      }
    },
    [debate],
  );

  const onCancel = useCallback(() => {
    debate.cancel();
  }, [debate]);

  // POST-FINALIZE LIFECYCLE ACTIONS.
  //
  // After the council finalises (or the operator stops it), the user
  // needs three distinct affordances which were previously missing:
  //
  //   • "Tekrar Çalıştır"  — keep wizard config + topology + query, just
  //                           re-launch the same debate (regression-free
  //                           A/B reproductions or "let's hear it again"
  //                           demos).
  //   • "Yeni Tartışma"    — full reset; both session AND wizard. Lands
  //                           on a clean draft state, opens the wizard.
  //   • "Sihirbazı Düzenle" — keep wizard config but step back to the
  //                           composer to tweak personas before re-running.
  //
  // All three live in the same slot the "Pause" button used while running.
  const onReplay = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      // Keep wizard, just reset session state and re-launch.
      resetForNew({ keepWizard: true });
      try {
        await debate.start({
          origin: "click",
          isTrusted: e.nativeEvent.isTrusted,
          timeStamp: e.nativeEvent.timeStamp,
        });
      } catch (err) {
        console.error("[macdm] failed to replay session:", err);
      }
    },
    [debate, resetForNew],
  );

  const onNewSession = useCallback(() => {
    resetForNew();
    openWizard();
  }, [resetForNew, openWizard]);

  const onEditWizard = useCallback(() => {
    resetForNew({ keepWizard: true });
    openWizard();
  }, [resetForNew, openWizard]);

  const submitPivot = useCallback(() => {
    if (!pivotDraft.trim()) return;
    stream.interrupt();
    setPivotDraft("");
    setPivotOpen(false);
  }, [pivotDraft, stream]);

  return (
    <header
      data-app-header
      className={cn(
        "sticky top-0 z-30 border-b border-line-soft/60",
        "bg-[hsl(var(--bg-page)/0.7)] backdrop-blur-xl",
      )}
      role="banner"
    >
      <div className="mx-auto flex max-w-[120rem] flex-wrap items-center justify-between gap-3 px-4 py-2.5 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3">
          <BrandMark />
          <div className="leading-tight">
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-fg-subtle">
              {t("app.brand")}
            </div>
            <h1 className="text-fluid-lg font-semibold text-fg-strong">{t("app.title")}</h1>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <StatusPill status={lifecycle.status} />

          <div className="hidden items-center gap-2 sm:flex">
            <Pill monospace tone={isRunning ? "accent" : "neutral"}>
              {t("header.roundShort")} {round} {t("header.of")} {maxRounds}
            </Pill>
            {/*
             * Compact finalize-reason badge — mirror of the one on the
             * roundtable, so the operator sees the WHY in the chrome too.
             * Only renders post-finalize; otherwise stays out of the way.
             */}
            {lifecycle.status === "finalized" && finalizeReason === "consensus" ? (
              <Pill monospace tone="success">Konsensüs · erken</Pill>
            ) : null}
            {lifecycle.status === "finalized" && finalizeReason === "deadlock" ? (
              <Pill monospace tone="warn">Deadlock · erken</Pill>
            ) : null}
            {lifecycle.status === "finalized" && finalizeReason === "user" ? (
              <Pill monospace tone="neutral">Durduruldu</Pill>
            ) : null}
            <span className="hidden items-center gap-1 rounded-full border border-line-soft/60 bg-surface-2/60 px-2.5 py-0.5 lg:inline-flex">
              <CompactConsensusGauge score={scalarScore} pending={pending} />
            </span>
          </div>

          {adminMode ? <Pill tone="warn" monospace>{t("status.dev")}</Pill> : null}

          <div className="flex items-center gap-1.5">
            <Tooltip label="Sunum modu (P)">
              <Button
                variant="ghost"
                size="icon"
                onClick={togglePresentMode}
                aria-label="Sunum modu"
                aria-pressed={presentMode}
              >
                <Maximize2 className="h-4 w-4" aria-hidden />
              </Button>
            </Tooltip>
            <Tooltip label="Klavye kısayolları (?)">
              <Button
                variant="ghost"
                size="icon"
                onClick={openShortcuts}
                aria-label="Klavye kısayollarını göster"
              >
                <Keyboard className="h-4 w-4" aria-hidden />
              </Button>
            </Tooltip>
            <Tooltip label={t("header.toggleTheme")}>
              <Button variant="ghost" size="icon" onClick={onToggleTheme} aria-label={t("header.toggleTheme")}>
                <Settings2 className="h-4 w-4" aria-hidden />
              </Button>
            </Tooltip>
            <Tooltip label={t("header.toggleMute")}>
              <Button variant="ghost" size="icon" onClick={onToggleMute} aria-label={t("header.toggleMute")}>
                {muted ? <VolumeX className="h-4 w-4" aria-hidden /> : <Volume2 className="h-4 w-4" aria-hidden />}
              </Button>
            </Tooltip>
          </div>

          {/*
           * Primary CTA — three discrete states, never overlapping:
           *
           *   1. Running  → big red "Durdur" (Pause)
           *   2. Wizard required (no personas yet) → "Sihirbazı Aç"
           *   3. Draft + personas ready  → "Tartışmayı Başlat"
           *   4. Finalized / interrupted → triplet (Tekrar Çalıştır
           *      · Yeni Tartışma · Sihirbazı Düzenle).
           */}
          {isRunning ? (
            <Button
              variant="danger"
              size="lg"
              leftIcon={<Pause className="h-4 w-4" aria-hidden />}
              onClick={onCancel}
              aria-label={t("header.stop")}
            >
              {t("header.stop")}
            </Button>
          ) : isFinalized ? (
            <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Tartışma sonrası aksiyonlar">
              <Tooltip label="Aynı konsey + aynı sorgu ile tekrar çalıştır">
                <Button
                  variant="primary"
                  size="md"
                  leftIcon={<RotateCcw className="h-4 w-4" aria-hidden />}
                  onClick={onReplay}
                  aria-label="Tekrar çalıştır"
                >
                  Tekrar Çalıştır
                </Button>
              </Tooltip>
              <Tooltip label="Konseyi düzenleyip yeniden başlat">
                <Button
                  variant="outline"
                  size="md"
                  leftIcon={<Wand2 className="h-4 w-4" aria-hidden />}
                  onClick={onEditWizard}
                  aria-label="Sihirbazı düzenle"
                >
                  Düzenle
                </Button>
              </Tooltip>
              <Tooltip label="Sıfırdan yeni tartışma — wizard'ı temizler">
                <Button
                  variant="ghost"
                  size="md"
                  leftIcon={<RefreshCw className="h-4 w-4" aria-hidden />}
                  onClick={onNewSession}
                  aria-label="Yeni tartışma başlat"
                >
                  Yeni Tartışma
                </Button>
              </Tooltip>
            </div>
          ) : requiresWizard ? (
            <Button
              ref={startBtnRef}
              data-cta="open-wizard"
              variant="primary"
              size="lg"
              leftIcon={<Wand2 className="h-4 w-4" aria-hidden />}
              onClick={openWizard}
              aria-label="Sihirbazı Aç"
            >
              Sihirbazı Aç
            </Button>
          ) : (
            <Button
              ref={startBtnRef}
              data-cta="start"
              variant="primary"
              size="lg"
              leftIcon={<Play className="h-4 w-4" aria-hidden />}
              onClick={onStart}
              disabled={!canStart}
              aria-label={t("header.start")}
            >
              {t("header.start")}
            </Button>
          )}

          {adminMode && isRunning && !interrupt.active ? (
            <Popover.Root open={pivotOpen} onOpenChange={setPivotOpen}>
              <Popover.Trigger asChild>
                <Button
                  variant="outline"
                  size="md"
                  leftIcon={<Zap className="h-3.5 w-3.5 text-amber-300" aria-hidden />}
                >
                  {t("header.pivot")}
                </Button>
              </Popover.Trigger>
              <Popover.Portal>
                <Popover.Content
                  align="end"
                  sideOffset={8}
                  className="z-50 w-[min(92vw,420px)] rounded-xl border border-line-soft/80 bg-surface-2/95 p-3 shadow-elev3 backdrop-blur"
                >
                  <label
                    htmlFor={pivotInputId}
                    className="mb-2 block text-[11px] uppercase tracking-wider text-fg-subtle"
                  >
                    {t("header.pivotPlaceholder")}
                  </label>
                  <div className="flex gap-2">
                    <input
                      id={pivotInputId}
                      type="text"
                      autoFocus
                      className="flex-1 rounded-lg border border-line-soft/70 bg-surface-0/60 px-3 py-1.5 text-sm text-fg-strong focus-ring"
                      placeholder={t("header.pivotPlaceholder")}
                      value={pivotDraft}
                      onChange={(e) => setPivotDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") submitPivot();
                      }}
                    />
                    <Button
                      variant="primary"
                      size="md"
                      onClick={submitPivot}
                      leftIcon={<Send className="h-3.5 w-3.5" aria-hidden />}
                    >
                      {t("header.pivotSend")}
                    </Button>
                  </div>
                </Popover.Content>
              </Popover.Portal>
            </Popover.Root>
          ) : null}

          {/* ─── User Navigation Menu ─── */}
          <div className="ml-1 border-l border-line-soft/50 pl-2.5">
            <Popover.Root open={userMenuOpen} onOpenChange={setUserMenuOpen}>
              <Popover.Trigger asChild>
                <button
                  className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm text-fg-muted hover:bg-surface-2/80 hover:text-fg-strong transition-colors"
                  aria-label="Kullanıcı menüsü"
                >
                  <div className="grid h-7 w-7 place-items-center rounded-full bg-[hsl(var(--accent)/0.15)] ring-1 ring-[hsl(var(--accent)/0.3)]">
                    <User className="h-3.5 w-3.5 text-[hsl(var(--accent))]" aria-hidden />
                  </div>
                  <span className="hidden text-xs font-medium sm:inline">
                    {authUser?.display_name ?? "Kullanıcı"}
                  </span>
                </button>
              </Popover.Trigger>
              <Popover.Portal>
                <Popover.Content
                  align="end"
                  sideOffset={8}
                  className="z-50 w-56 rounded-xl border border-line-soft/80 bg-surface-2/95 p-1.5 shadow-elev3 backdrop-blur-xl"
                >
                  <div className="px-3 py-2 border-b border-line-soft/50 mb-1">
                    <p className="text-xs font-medium text-fg-strong truncate">{authUser?.display_name}</p>
                    <p className="text-[10px] text-fg-subtle truncate">{authUser?.email}</p>
                  </div>
                  <button
                    onClick={() => { setUserMenuOpen(false); navigate("/"); }}
                    className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-fg-muted hover:bg-surface-3/80 hover:text-fg-strong transition-colors"
                  >
                    <History className="h-4 w-4" aria-hidden />
                    Geçmiş Tartışmalar
                  </button>
                  <button
                    onClick={() => { setUserMenuOpen(false); navigate("/settings"); }}
                    className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-fg-muted hover:bg-surface-3/80 hover:text-fg-strong transition-colors"
                  >
                    <Settings2 className="h-4 w-4" aria-hidden />
                    Ayarlar
                  </button>
                  {authUser?.role === "admin" && (
                    <button
                      onClick={() => { setUserMenuOpen(false); navigate("/admin"); }}
                      className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-fg-muted hover:bg-surface-3/80 hover:text-fg-strong transition-colors"
                    >
                      <User className="h-4 w-4" aria-hidden />
                      Kullanıcı Yönetimi
                    </button>
                  )}
                  <div className="border-t border-line-soft/50 mt-1 pt-1">
                    <button
                      onClick={async () => {
                        setUserMenuOpen(false);
                        await authLogout();
                        navigate("/login", { replace: true });
                      }}
                      className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
                    >
                      <LogOut className="h-4 w-4" aria-hidden />
                      Çıkış Yap
                    </button>
                  </div>
                </Popover.Content>
              </Popover.Portal>
            </Popover.Root>
          </div>
        </div>
      </div>
    </header>
  );
}
